// src/components/purchases/BulkCreateFromCatalogModal.tsx
// Modal for creating initial bulk stock from a purchase line item
// Pre-populated from product catalog - only asks for location details

import React, { useState, useEffect } from 'react';
import { X, Package, MapPin, AlertTriangle, Plus } from 'lucide-react';
import { Product } from '../../services/productCatalogAPI';
import { fulfillmentAPI } from '../../services/fulfillmentAPI';
import { PurchaseLineItem } from '../../types/purchase';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';

interface BulkCreateFromCatalogModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  lineItem: PurchaseLineItem;
  purchaseId: string;
  onComplete: (stockId: string) => void;
}

const BulkCreateFromCatalogModal: React.FC<BulkCreateFromCatalogModalProps> = ({
  isOpen,
  onClose,
  product,
  lineItem,
  purchaseId,
  onComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state - pre-populated from product catalog
  const [location, setLocation] = useState(product.defaultLocation || '');
  const [subLocation, setSubLocation] = useState('');
  const [minStockLevel, setMinStockLevel] = useState<number | ''>(product.minStockLevel || '');
  const [notes, setNotes] = useState('');

 // Quantity from line item
  const quantity = lineItem.quantity - lineItem.allocatedQuantity;

  // Storage locations from tenant config
  const [storageLocations, setStorageLocations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  // Load storage locations on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locations = await tenantConfigAPI.getInventoryLocations();
        setStorageLocations(locations);
      } catch (err) {
        console.error('Failed to load storage locations:', err);
      } finally {
        setLocationsLoading(false);
      }
    };
    loadLocations();
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocation(product.defaultLocation || '');
      setSubLocation('');
      setMinStockLevel(product.minStockLevel || '');
      setNotes('');
      setError(null);
    }
  }, [isOpen, product]);

  const handleSubmit = async () => {
    // Validate required fields
    if (!location.trim()) {
      setError('Location is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fulfillmentAPI.createBulkStock({
        purchaseId,
        lineItemId: lineItem.lineItemId,
        productId: product.productId,
        quantity,
        location: location.trim(),
        subLocation: subLocation.trim() || undefined,
        minStockLevel: typeof minStockLevel === 'number' ? minStockLevel : undefined,
        notes: notes.trim() || undefined,
        categoryName: lineItem.categoryName,
        subcategoryName: lineItem.subcategoryName,
        categoryId: lineItem.categoryId,
        subcategoryId: lineItem.subcategoryId
      });

      onComplete(response.stockId);
      onClose();
    } catch (err: any) {
      console.error('Create bulk stock failed:', err);
      setError(err.message || 'Failed to create bulk stock');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Create Initial Stock</h3>
          </div>
          <button 
            onClick={onClose} 
            className="p-1 hover:bg-gray-100 rounded"
            disabled={loading}
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Product Info (read-only) */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Package className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{product.name}</h4>
                {(product.manufacturer || product.model) && (
                  <p className="text-sm text-gray-600">
                    {[product.manufacturer, product.model].filter(Boolean).join(' • ')}
                  </p>
                )}
                <div className="mt-2 flex items-center space-x-4 text-sm">
                  <span className={`px-2 py-0.5 rounded ${
                    product.trackingType === 'CONSUMABLE'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {product.trackingType === 'CONSUMABLE' ? 'Consumable' : 'Bulk Tracked'}
                  </span>
                  <span className="text-gray-600">
                    Quantity: <span className="font-medium text-gray-900">{quantity.toLocaleString()}</span>
                    {product.unitOfMeasure && ` ${product.unitOfMeasure}`}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Location (required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="flex items-center space-x-1">
                <MapPin className="w-4 h-4" />
                <span>Primary Location <span className="text-red-500">*</span></span>
              </span>
            </label>
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              disabled={locationsLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              <option value="">Select storage location...</option>
              {storageLocations.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
            {locationsLoading && (
              <p className="text-xs text-gray-500 mt-1">Loading locations...</p>
            )}
            {!locationsLoading && storageLocations.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No storage locations configured. Add them in Admin → Tenant Settings.
              </p>
            )}
          </div>

          {/* Sub-location (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sub-Location <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={subLocation}
              onChange={(e) => setSubLocation(e.target.value)}
              placeholder="e.g., Shelf B-3, Bin 12"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Min Stock Level (optional, pre-filled from catalog) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Stock Level <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="number"
              min="0"
              value={minStockLevel}
              onChange={(e) => setMinStockLevel(e.target.value ? parseInt(e.target.value) : '')}
              placeholder="Alert when stock falls below this level"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {product.minStockLevel && (
              <p className="text-xs text-gray-500 mt-1">
                Default from catalog: {product.minStockLevel.toLocaleString()}
              </p>
            )}
          </div>

          {/* Notes (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any additional notes about this stock..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Summary */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-800">
              <span className="font-medium">{quantity.toLocaleString()}</span>
              {product.unitOfMeasure && ` ${product.unitOfMeasure}`} will be added to inventory
              {location && ` at ${location}`}
              {subLocation && ` (${subLocation})`}.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !location.trim()}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Create Stock</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkCreateFromCatalogModal;
